// src/App.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import Login from './components/Login';
// Renamed purely in import for clarity, though file remains Dashboard.js
import ExtractionReport from './pages/ExtractionReport';
import ContentMappingReport from './pages/ContentMappingReport';
import Tutorial from './pages/Tutorial';
import SlideshowPlayer from './pages/SlideshowPlayer';
import StudentSlideshowPlayer from './pages/StudentSlideshowPlayer';
import StudentTutorialPlayer from './pages/StudentTutorialPlayer';
import CostTracker from './pages/CostTracker';
import HomeworkWhatIf from './pages/HomeworkWhatIf';
import { authAPI, activityAPI } from './services/api';

import ActivityHeartbeat from './components/ActivityHeartbeat';
import { onAuthStateChanged, signInWithCustomToken, signOut } from 'firebase/auth';
import { auth } from './firebase';
import { identifyPostHogUser, resetPostHogUser } from './utils/posthog';
import CookieConsent from './components/CookieConsent';
import { getStoredConsent, applyConsent } from './utils/consent';
import { applyGuideLoginCount, claimGuideLogin, endGuideLogin, suppressAutoGuideForUser } from './utils/guideSession';
import { getSltViewAs, clearSltViewAs } from './utils/sltViewAs';
import { canAccessJcq } from './utils/jcqAccess';

import PerformanceTracker from './pages/PerformanceTracker';
import PreTeachingReport from './pages/PreTeachingReport';
import StudentDashboard from './pages/StudentDashboard';
import LandingPage from './pages/LandingPage'; 
import RegisterInterest from './pages/RegisterInterest';
import SensibleSencoReview from './pages/SensibleSencoReview';
import PrivacyPolicy from './pages/PrivacyPolicy';

// +++ IMPORT DEMO PAGES +++
import StandardDashboard from './pages/standard/StandardDashboard';
import LiteDashboard from './pages/lite/LiteDashboard';
import StandardLessonPlanDetail from './pages/standard/StandardLessonPlanDetail';
import LiteLessonPlanDetail from './pages/lite/LiteLessonPlanDetail';
import StandardStudents from './pages/standard/StandardStudents';
import LiteStudents from './pages/lite/LiteStudents';
import DemoHomeworkDetail from './pages/demo/DemoHomeworkDetail';
import DemoContentLibrary from './pages/demo/DemoContentLibrary';
import DemoContentLibraryHomework from './pages/demo/DemoContentLibraryHomework';
import ClassLinkPage from './pages/ClassLinkPage';
// import VocationalDemo from './pages/VocationalDemo'; // route retired — kept for reference
import JCQHub from './pages/JCQHub';
import JCQExtraTimeTest from './pages/JCQExtraTimeTest';
import JCQNormalWayOfWorking from './pages/JCQNormalWayOfWorking';
import JCQStudentChecklist from './pages/JCQStudentChecklist';
import SLTOverview from './pages/slt/SLTOverview';
import SLTTeachers from './pages/slt/SLTTeachers';
import SLTStudents from './pages/slt/SLTStudents';
import SLTTeacherDetail from './pages/slt/SLTTeacherDetail';
import SLTStudentDetail from './pages/slt/SLTStudentDetail';

// Tier classification by registration code.
const LITE_REGISTRATION_CODE = 'CL26';

const IMPERSONATION_PICKER_EMAILS = String(process.env.REACT_APP_IMPERSONATION_PICKER_EMAILS || '')
  .split(',')
  .map((email) => email.trim().toLowerCase())
  .filter(Boolean);

const STATIC_PUBLIC_ROUTES = new Set([
  '',
  'register-interest',
  'privacy-policy',
  'sensiblesencoreview',
  'login',
  'nacro-login',
  'demo',
  'dashboard',
  'lite',
  'lessons',
  'students',
  'profiles',
  'content-library',
  'lesson-plan',
  'homework',
  'homework-schedule',
  'student-dashboard',
  'extraction-report',
  'content-mapping',
  'tutorial',
  'slideshow',
  'student-slideshow',
  'student-tutorial-player',
  'share',
  'tutorial-report',
  'cost-tracker',
  'jcq-hub',
  'jcq',
  'slt'
]);

const isLikelySharedLinkPath = (pathname = '') => {
  const safePath = String(pathname || '').trim();
  if (!safePath || safePath === '/') return false;

  const [segment] = safePath.replace(/^\//, '').split('/');
  if (!segment) return false;

  return !STATIC_PUBLIC_ROUTES.has(segment);
};

const isExplicitSharedLinkPath = (pathname = '') => String(pathname || '').startsWith('/share/');

const isSharedLinkPath = (pathname = '') => isExplicitSharedLinkPath(pathname) || isLikelySharedLinkPath(pathname);


const NavigateToStudents = () => {
  const { scheduleId } = useParams();
  return <Navigate to={`/students/${scheduleId}`} replace />;
};

const NavigateToLiteProfiles = () => {
  const { scheduleId } = useParams();
  return <Navigate to={`/lite/profiles/${scheduleId}`} replace />;
};

const NavigateToTutorialReport = () => {
  const { studentTutorialId } = useParams();
  return <Navigate to={`/tutorial-report/${studentTutorialId}`} replace />;
};

const AppContent = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [activeTier, setActiveTier] = useState('STANDARD');
  const [showImpersonationPicker, setShowImpersonationPicker] = useState(false);
  const [impersonationUsers, setImpersonationUsers] = useState([]);
  const [impersonationTargetUid, setImpersonationTargetUid] = useState('');
  const [impersonationReason, setImpersonationReason] = useState('Support dashboard verification');
  const [impersonationLoading, setImpersonationLoading] = useState(false);
  const [impersonationError, setImpersonationError] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const guideLoginPromisesRef = useRef({});

  const recordGuideLoginForUser = useCallback(async (uid) => {
    if (!uid) return null;

    if (guideLoginPromisesRef.current[uid]) {
      return guideLoginPromisesRef.current[uid];
    }

    if (!claimGuideLogin(uid)) {
      return null;
    }

    const recordPromise = authAPI.recordGuideLogin()
      .then((response) => {
        const count = Number(response?.data?.guideLoginCount);
        if (!Number.isFinite(count)) {
          return suppressAutoGuideForUser(uid);
        }
        return applyGuideLoginCount(uid, count);
      })
      .catch((error) => {
        console.warn('Failed to record guide login; suppressing automatic guide for this session:', error);
        return suppressAutoGuideForUser(uid);
      })
      .finally(() => {
        delete guideLoginPromisesRef.current[uid];
      });

    guideLoginPromisesRef.current[uid] = recordPromise;
    return recordPromise;
  }, []);


  // PostHog is initialised lazily by identifyPostHogUser() on login, so it only
  // ever captures authenticated (teacher/admin) sessions — never anonymous
  // marketing visitors or accountless pupil magic-link sessions.

  // Cookie consent: Consent Mode defaults are set in index.html <head> (before
  // GTM). Here we just re-apply any previously stored decision so tags resume.
  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) applyConsent(stored);
  }, []);

  // SPA route changes: push a virtual pageview to the dataLayer so GTM tags
  // (e.g. the Meta Pixel) fire on client-side navigation, not just first load.
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'spa_pageview', page_path: location.pathname });
  }, [location.pathname]);

  const shouldShowImpersonationPicker = (email) => {
    if (!email) return false;
    return IMPERSONATION_PICKER_EMAILS.includes(String(email).toLowerCase());
  };

  const loadImpersonationUsers = async () => {
    setImpersonationError('');
    try {
      const response = await authAPI.getImpersonationUsers();
      const users = Array.isArray(response?.data?.users) ? response.data.users : [];
      setImpersonationUsers(users);
      if (users.length > 0 && !impersonationTargetUid) {
        setImpersonationTargetUid(users[0].uid);
      }
    } catch (error) {
      setImpersonationError(error?.response?.data?.error || 'Unable to load users for impersonation.');
    }
  };

  const determineInitialTier = (registrationCodeUsed = '') => {
    const code = String(registrationCodeUsed || '').trim().toUpperCase();
    return code === LITE_REGISTRATION_CODE ? 'LITE' : 'STANDARD';
  };

  // SLT accounts see the oversight dashboard instead of the teaching dashboards.
  const isSltUser = (u) => String(u?.role || '').trim().toUpperCase() === 'SLT';

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const res = await authAPI.getProfile();
          const userProfile = res.data.user;
          const recordedGuideLoginCount = await recordGuideLoginForUser(firebaseUser.uid);
          const guideLoginCount = recordedGuideLoginCount ?? userProfile?.guideLoginCount ?? null;
          setUser({ ...userProfile, guideLoginCount });

          // CL26 accounts are permanently locked to anonymous-profile mode + lower reading age.
          // Force the localStorage values on every session restore so they can never drift.
          const normalizedRegistrationCode = (userProfile?.registrationCodeUsed || '').trim().toUpperCase();
          if (normalizedRegistrationCode === 'CL26') {
              const uid = firebaseUser.uid;
              localStorage.setItem(`studentProfileMode_${uid}`, 'supply_teacher');
              localStorage.setItem(`readingAgeMode_${uid}`, 'lower');
          } else if (normalizedRegistrationCode === 'TEST R_26') {
              const uid = firebaseUser.uid;
              localStorage.setItem(`studentProfileMode_${uid}`, 'default');
              localStorage.setItem(`readingAgeMode_${uid}`, 'default');
              localStorage.setItem(`documentUploadMode_${uid}`, 'standard');
          }
          identifyPostHogUser(firebaseUser.uid, userProfile?.email || firebaseUser.email);

          const initialTier = determineInitialTier(userProfile.registrationCodeUsed);
          setActiveTier(initialTier);
          setIsDemoMode(initialTier === 'LITE');

          if (shouldShowImpersonationPicker(userProfile.email)) {
            setShowImpersonationPicker(true);
            loadImpersonationUsers();
          } else {
            setShowImpersonationPicker(false);
          }

          if (isSltUser(userProfile) && !getSltViewAs()?.teacherUid && !location.pathname.startsWith('/slt') && !isSharedLinkPath(location.pathname)) {
            navigate('/slt/dashboard');
          } else if (initialTier === 'LITE' && !location.pathname.startsWith('/lite') && !isSharedLinkPath(location.pathname)) {
            navigate('/lite/dashboard');
          }

        } catch (error) {
          console.error("Session profile refresh failed; keeping the Firebase session active:", error);
          const guideLoginCount = await recordGuideLoginForUser(firebaseUser.uid);
          setUser((previousUser) => {
            const shouldPreserveProfile = previousUser?.uid === firebaseUser.uid;
            return {
              ...(shouldPreserveProfile ? previousUser : {}),
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              email_verified: firebaseUser.emailVerified,
              sessionProfileUnavailable: true,
              guideLoginCount
            };
          });
          identifyPostHogUser(firebaseUser.uid, firebaseUser.email);
          setShowImpersonationPicker(false);
        }
      } else {
        setUser(null);
        setShowImpersonationPicker(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [location.pathname, navigate, recordGuideLoginForUser]);

  // +++ NEW: Automatic Navigation Tracking +++
  useEffect(() => {
    if (user && location.pathname && !isSharedLinkPath(location.pathname)) {
      activityAPI.logEvent('NAVIGATION', location.pathname)
        .catch(err => console.warn('Failed to log navigation event:', err));
    }
  }, [location.pathname, user]);

  const handleLoginSuccess = async (token, loggedInUser) => {
    const recordedGuideLoginCount = auth.currentUser?.uid
      ? await recordGuideLoginForUser(auth.currentUser.uid)
      : null;
    const guideLoginCount = recordedGuideLoginCount ?? loggedInUser?.guideLoginCount ?? null;
    setUser({ ...loggedInUser, guideLoginCount });
    identifyPostHogUser(auth.currentUser?.uid, loggedInUser?.email || auth.currentUser?.email);

    const normalizedRegistrationCode = (loggedInUser?.registrationCodeUsed || '').trim().toUpperCase();
    if (auth.currentUser?.uid && normalizedRegistrationCode === 'CL26') {
      localStorage.setItem(`studentProfileMode_${auth.currentUser.uid}`, 'supply_teacher');
      localStorage.setItem(`readingAgeMode_${auth.currentUser.uid}`, 'lower');
    } else if (auth.currentUser?.uid && normalizedRegistrationCode === 'TEST R_26') {
      localStorage.setItem(`studentProfileMode_${auth.currentUser.uid}`, 'default');
      localStorage.setItem(`readingAgeMode_${auth.currentUser.uid}`, 'default');
      localStorage.setItem(`documentUploadMode_${auth.currentUser.uid}`, 'standard');
    }

    const initialTier = determineInitialTier(loggedInUser.registrationCodeUsed);
    setActiveTier(initialTier);
    setIsDemoMode(initialTier === 'LITE');

    if (shouldShowImpersonationPicker(loggedInUser.email)) {
      setShowImpersonationPicker(true);
      loadImpersonationUsers();
    } else {
      setShowImpersonationPicker(false);
    }

    navigate(isSltUser(loggedInUser) ? '/slt/dashboard' : (initialTier === 'LITE' ? '/lite/dashboard' : '/dashboard'));
  };


  const handleStartImpersonation = async () => {
    if (!impersonationTargetUid) {
      setImpersonationError('Select a user to impersonate.');
      return;
    }

    setImpersonationLoading(true);
    setImpersonationError('');

    try {
      const response = await authAPI.createImpersonationToken({
        targetUid: impersonationTargetUid,
        reason: impersonationReason || 'Support dashboard verification'
      });

      const customToken = response?.data?.customToken;
      if (!customToken) {
        throw new Error('No impersonation token received.');
      }

      await signInWithCustomToken(auth, customToken);
      setShowImpersonationPicker(false);
    } catch (error) {
      setImpersonationError(error?.response?.data?.error || error?.message || 'Impersonation failed.');
    } finally {
      setImpersonationLoading(false);
    }
  };




  const handleLogout = async () => {
    try {
      await activityAPI.logEvent('ACTION', 'LOGOUT', {
        action: 'LOGOUT',
        route: window.location.pathname,
      });
    } catch (error) {
      // Never block sign-out on telemetry failure.
      console.warn('Failed to log logout event:', error);
    }

    try {
      clearSltViewAs();
      await signOut(auth);
      localStorage.removeItem('authToken');
      setUser(null);
      resetPostHogUser();
      endGuideLogin();
      setIsDemoMode(false);
      navigate('/login');
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const ProtectedRoute = ({ children }) => {
    if (loading) return <div style={{padding:'20px'}}>Loading Session...</div>;
    return user ? children : <Navigate to="/login" />;
  };
  const StandardGuard = ({ children }) => {
    if (loading) return <div style={{padding:'20px'}}>Loading Session...</div>;
    if (!user) return <Navigate to="/login" />;
    if (isSltUser(user)) {
      // SLT users may explore a teacher's workspace read-only (X-SLT-View-As).
      if (getSltViewAs()?.teacherUid) return children;
      return <Navigate to="/slt/dashboard" replace />;
    }
    if (activeTier !== 'STANDARD') return <Navigate to="/lite/dashboard" replace />;
    return children;
  };
  const LiteGuard = ({ children }) => {
    if (loading) return <div style={{padding:'20px'}}>Loading Session...</div>;
    if (!user) return <Navigate to="/login" />;
    if (isSltUser(user)) return <Navigate to="/slt/dashboard" replace />;
    if (activeTier !== 'LITE') return <Navigate to="/dashboard" replace />;
    return children;
  };
  const SltGuard = ({ children }) => {
    if (loading) return <div style={{padding:'20px'}}>Loading Session...</div>;
    if (!user) return <Navigate to="/login" />;
    if (!isSltUser(user)) return <Navigate to={activeTier === 'LITE' ? '/lite/dashboard' : '/dashboard'} replace />;
    return children;
  };
  const JcqGuard = ({ children }) => {
    if (loading) return <div style={{padding:'20px'}}>Loading Session...</div>;
    if (!user) return <Navigate to="/login" />;
    if (!canAccessJcq(user)) return <Navigate to={activeTier === 'LITE' ? '/lite/dashboard' : '/dashboard'} replace />;
    return children;
  };

  // Global floating logout is intentionally disconnected to avoid duplicate logout controls
  // across dashboard/lesson pages that already render an inline logout button.
  const showGlobalLogout = false;

  // While an SLT user explores a teacher's workspace read-only, teacher pages
  // should greet/display the viewed teacher, not the SLT user.
  const sltViewAsCtx = user && isSltUser(user) ? getSltViewAs() : null;
  const effectiveUser = sltViewAsCtx?.teacherUid
    ? { ...user, firstName: sltViewAsCtx.teacherName, lastName: '', displayName: sltViewAsCtx.teacherName }
    : user;

  return (
    <div>
      <ActivityHeartbeat />
      <CookieConsent />

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@700;800&display=swap');
          
          .app-logout-btn {
            background: white;
            color: #2D4042;
            border: 2px solid #E0E0E0;
            border-bottom: 4px solid #E0E0E0; 
            padding: 8px 20px;
            border-radius: 10px;
            font-family: 'Nunito', sans-serif;
            font-weight: 800;
            font-size: 0.95rem;
            cursor: pointer;
            transition: all 0.1s ease;
            z-index: 1000;
          }
          
          .app-logout-btn:hover {
            transform: translateY(1px);
            border-bottom: 3px solid #E0E0E0;
            color: #E07A5F;
            border-color: #E07A5F;
          }
          
          .app-logout-btn:active {
            transform: translateY(3px);
            border-bottom: 0px solid transparent;
            margin-top: 3px;
          }
        `}
      </style>

      {showGlobalLogout && (
        <div style={{ 
            maxWidth: '1200px', 
            margin: '0 auto', 
            padding: '0 40px', 
            position: 'relative', 
            height: '0', 
            pointerEvents: 'none' 
        }}>
            <button 
                onClick={handleLogout} 
                className="app-logout-btn"
                style={{
                    pointerEvents: 'auto', 
                    position: 'absolute',
                    top: '40px',
                    right: '40px' 
                }}
            >
                Log Out
            </button>
        </div>
      )}
      

      {showImpersonationPicker && user && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', width: '100%', maxWidth: 520, borderRadius: 12, border: '2px solid #2D4042', boxShadow: '0 14px 30px rgba(0,0,0,0.2)', padding: 20 }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#2D4042' }}>Support Impersonation</h3>
            <p style={{ marginTop: 0, color: '#556', fontSize: 14 }}>Logged in as <strong>{user.email}</strong>. Pick a user account to inspect.</p>

            <label style={{ display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>User</label>
            <select
              value={impersonationTargetUid}
              onChange={(e) => setImpersonationTargetUid(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ccd' }}
            >
              <option value="">Select user...</option>
              {impersonationUsers.map((u) => (
                <option key={u.uid} value={u.uid}>{u.email}{u.name ? ` — ${u.name}` : ''}</option>
              ))}
            </select>

            <label style={{ display: 'block', fontSize: 13, fontWeight: 700, marginTop: 12, marginBottom: 6 }}>Reason (audited)</label>
            <input
              value={impersonationReason}
              onChange={(e) => setImpersonationReason(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ccd' }}
              placeholder="Why are you impersonating this user?"
            />

            {impersonationError && <p style={{ color: '#B00020', marginTop: 10 }}>{impersonationError}</p>}

            <div style={{ marginTop: 14, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={loadImpersonationUsers} className="app-logout-btn" style={{ position: 'static' }}>Refresh</button>
              <button onClick={handleLogout} className="app-logout-btn" style={{ position: 'static' }}>Cancel & Log Out</button>
              <button onClick={handleStartImpersonation} className="app-logout-btn" style={{ position: 'static', background: '#75B9BE', color: '#fff', borderColor: '#2D4042' }} disabled={impersonationLoading}>
                {impersonationLoading ? 'Signing in...' : 'Impersonate'}
              </button>
            </div>
          </div>
        </div>
      )}



      {user && isSltUser(user) && getSltViewAs()?.teacherUid && !location.pathname.startsWith('/slt') && (
        <div style={{ alignItems: 'center', background: '#2D4042', color: '#FFFFFF', display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', padding: '10px 16px', position: 'sticky', top: 0, zIndex: 3000 }}>
          <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800 }}>
            👁 Read-only SLT view — exploring {getSltViewAs()?.teacherName || 'a teacher'}'s workspace. Changes are disabled.
          </span>
          <button
            type="button"
            onClick={() => {
              const ctx = getSltViewAs();
              clearSltViewAs();
              navigate(ctx?.teacherUid ? `/slt/teachers/${ctx.teacherUid}` : '/slt/dashboard');
            }}
            style={{ background: '#FF7F50', border: 'none', borderRadius: '999px', color: '#FFFFFF', cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontWeight: 800, padding: '6px 16px' }}
          >
            Exit teacher view
          </button>
        </div>
      )}

      <Routes>
        {/* +++ UPDATED ROOT ROUTE +++ */}
        <Route path="/" element={user ? <Navigate to={isSltUser(user) ? '/slt/dashboard' : (activeTier === 'LITE' ? '/lite/dashboard' : '/dashboard')} /> : <LandingPage />} />
        
<Route path="/register-interest" element={<RegisterInterest />} />
        <Route path="/sensiblesencoreview" element={<SensibleSencoReview />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

        <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
        {/* NACRO portal retired (15 Oct 2026): old links land on the normal login. */}
        <Route path="/nacro-login" element={<Navigate to="/login" replace />} />
        
        {/* +++ SANDBOX ROUTES (Restricted via Redirect Logic) +++ */}
        <Route path="/dashboard" element={<StandardGuard><StandardDashboard user={effectiveUser} onLogout={handleLogout} /></StandardGuard>} />
        <Route path="/lesson-plan/:id" element={<Navigate to="/dashboard" replace />} />
        <Route path="/lessons/:id" element={<StandardGuard><StandardLessonPlanDetail user={effectiveUser} onLogout={handleLogout} /></StandardGuard>} />
        <Route path="/profiles/:scheduleId" element={<StandardGuard><NavigateToStudents /></StandardGuard>} />
        <Route path="/students/:scheduleId" element={<StandardGuard><StandardStudents user={effectiveUser} onLogout={handleLogout} /></StandardGuard>} />
        <Route path="/content-library" element={<StandardGuard><DemoContentLibrary user={effectiveUser} onLogout={handleLogout} /></StandardGuard>} />
        <Route path="/content-library/:id/view" element={<StandardGuard><DemoContentLibraryHomework user={effectiveUser} onLogout={handleLogout} /></StandardGuard>} />
        <Route path="/homework/:homeworkId" element={<StandardGuard><DemoHomeworkDetail user={effectiveUser} onLogout={handleLogout} isDemoMode={false} /></StandardGuard>} />

        <Route path="/homework-schedule/:scheduleId" element={<StandardGuard><NavigateToStudents /></StandardGuard>} />
        <Route path="/student-dashboard" element={<ProtectedRoute><StudentDashboard onLogout={handleLogout} /></ProtectedRoute>} />
        <Route path="/extraction-report/:lessonPlanId" element={<ProtectedRoute><ExtractionReport /></ProtectedRoute>} />
        <Route path="/homework/:homeworkId/what-if" element={<ProtectedRoute><HomeworkWhatIf /></ProtectedRoute>} />
        <Route path="/content-mapping/:lessonPlanId" element={<ProtectedRoute><ContentMappingReport /></ProtectedRoute>} />
        <Route path="/tutorial/:homeworkId" element={<ProtectedRoute><Tutorial /></ProtectedRoute>} />
        <Route path="/slideshow/:homeworkId/:chunkIndex" element={<ProtectedRoute><SlideshowPlayer /></ProtectedRoute>} />
        <Route path="/student-slideshow/:studentTutorialId/:chunkIndex" element={<ProtectedRoute><StudentSlideshowPlayer /></ProtectedRoute>} />
        
        {/* +++ UPDATED: Pass user to StudentTutorialPlayer +++ */}
        <Route path="/student-tutorial-player/:studentTutorialId" element={<ProtectedRoute><StudentTutorialPlayer user={user} /></ProtectedRoute>} />
        <Route path="/lite/student-tutorial-player/:studentTutorialId" element={<LiteGuard><StudentTutorialPlayer user={user} /></LiteGuard>} />
        <Route path="/demo/student-tutorial-player/:studentTutorialId" element={<ProtectedRoute><StudentTutorialPlayer user={user} /></ProtectedRoute>} />

        <Route path="/tutorial/:tutorialId/progress/:studentId" element={<ProtectedRoute><PerformanceTracker /></ProtectedRoute>} />
        <Route path="/tutorial-report/:studentTutorialId" element={<ProtectedRoute><PreTeachingReport onLogout={handleLogout} /></ProtectedRoute>} />
        <Route path="/pre-teaching-report/:studentTutorialId" element={<ProtectedRoute><NavigateToTutorialReport /></ProtectedRoute>} />
        <Route path="/cost-tracker" element={<ProtectedRoute><CostTracker /></ProtectedRoute>} />
        <Route path="/slt/dashboard" element={<SltGuard><SLTOverview user={user} onLogout={handleLogout} /></SltGuard>} />
        <Route path="/slt/teachers" element={<SltGuard><SLTTeachers user={user} onLogout={handleLogout} /></SltGuard>} />
        <Route path="/slt/students" element={<SltGuard><SLTStudents user={user} onLogout={handleLogout} /></SltGuard>} />
        <Route path="/slt/teachers/:teacherUid" element={<SltGuard><SLTTeacherDetail user={user} onLogout={handleLogout} /></SltGuard>} />
        <Route path="/slt/students/:studentId" element={<SltGuard><SLTStudentDetail user={user} onLogout={handleLogout} /></SltGuard>} />
        <Route path="/jcq-hub" element={<JcqGuard><JCQHub /></JcqGuard>} />
        <Route path="/jcq/extra-time-test" element={<JcqGuard><JCQExtraTimeTest /></JcqGuard>} />
        <Route path="/jcq/nnow" element={<JcqGuard><JCQNormalWayOfWorking /></JcqGuard>} />
        <Route path="/jcq/student-checklist" element={<JcqGuard><JCQStudentChecklist /></JcqGuard>} />

        <Route path="/lite/dashboard" element={<LiteGuard><LiteDashboard user={user} onLogout={handleLogout} /></LiteGuard>} />
        <Route path="/lite/lesson-plan/:id" element={<Navigate to="/lite/dashboard" replace />} />
        <Route path="/lite/homework-schedule/:scheduleId" element={<LiteGuard><NavigateToLiteProfiles /></LiteGuard>} />
        <Route path="/lite/lessons/:id" element={<LiteGuard><LiteLessonPlanDetail user={user} onLogout={handleLogout} /></LiteGuard>} />
        <Route path="/lite/profiles/:scheduleId" element={<LiteGuard><LiteStudents user={user} onLogout={handleLogout} /></LiteGuard>} />
        <Route path="/lite/students/:scheduleId" element={<LiteGuard><NavigateToLiteProfiles /></LiteGuard>} />
        <Route path="/lite/content-library" element={<LiteGuard><DemoContentLibrary user={user} onLogout={handleLogout} /></LiteGuard>} />
        <Route path="/lite/content-library/:id/view" element={<LiteGuard><DemoContentLibraryHomework user={user} onLogout={handleLogout} /></LiteGuard>} />
        <Route path="/lite/homework/:homeworkId" element={<LiteGuard><DemoHomeworkDetail user={user} onLogout={handleLogout} isDemoMode={true} /></LiteGuard>} />
        <Route path="/demo/dashboard" element={<Navigate to="/dashboard" replace />} />
        <Route path="/demo/lesson-plan/:id" element={<Navigate to="/dashboard" replace />} />
        <Route path="/demo/homework-schedule/:scheduleId" element={<Navigate to="/dashboard" replace />} />
        <Route path="/demo/homework/:homeworkId" element={
            <ProtectedRoute>
                <DemoHomeworkDetail
                    user={user}
                    onLogout={handleLogout}
                    isDemoMode={true}
                />
            </ProtectedRoute>
        } />

        <Route path="/demo/content-library" element={
            <ProtectedRoute>
                <DemoContentLibrary user={user} onLogout={handleLogout} />
            </ProtectedRoute>
        } />

        <Route path="/demo/content-library/:id/view" element={
            <ProtectedRoute>
                <DemoContentLibraryHomework user={user} onLogout={handleLogout} />
            </ProtectedRoute>
        } />

        {/* Public shared student tutorial link (no teacher zone) */}
        {/* /vocationaldemo route retired — component kept in ./pages/VocationalDemo but not hosted */}
        <Route path="/share/:shareToken" element={<StudentTutorialPlayer user={user} sharedMode={true} />} />
        <Route path="/class-link/:token" element={<ClassLinkPage />} />

        {/* Backward compatibility for previously sent links */}
        <Route path="/:shareToken" element={<StudentTutorialPlayer user={user} sharedMode={true} />} />

        <Route path="*" element={<Navigate to={user ? (isSltUser(user) ? '/slt/dashboard' : (activeTier === 'LITE' ? "/lite/dashboard" : "/dashboard")) : "/login"} />} />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
